import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import RegionPicker from '../components/RegionPicker';
import { toast } from '../utils/toast';

/**
 * Salary Component Masters — two-level priority lookup:
 *   L1 — schema + component + region (employee location) + dates → rate
 *   L2 — schema + component + region (company)            + dates → rate
 *
 * Each row stores either rate_pct (%) OR fixed_value (flat amount).
 * Resolver tries L1 → L2 → 0% with red flag.
 *
 * Two route modes (same pattern as Pricing Component Masters):
 *   - #/salary-schemas/component-masters         → all schemas
 *   - #/salary-schemas/:schemaId/component-masters → schema-locked
 */
export default function SalaryComponentMasters() {
  const lockedSchemaId = parseLockedSchemaId();

  const [level, setLevel]           = useState(1);
  const [schemas, setSchemas]       = useState([]);
  const [components, setComponents] = useState([]);
  const [rates, setRates]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [draft, setDraft]           = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, ratesRes] = await Promise.all([
        apiFetch('/api/salary-schemas'),
        apiFetch(lockedSchemaId
          ? `/api/salary-schemas/${lockedSchemaId}/rates?level=${level}`
          : `/api/salary-schemas/rates/all?level=${level}`),
      ]);
      if (sRes.ok)     setSchemas((await sRes.json()).schemas || []);
      if (ratesRes.ok) setRates((await ratesRes.json()).rates  || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [lockedSchemaId, level]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchComponents = useCallback(async (schemaId) => {
    if (!schemaId) { setComponents([]); return; }
    try {
      const r = await apiFetch(`/api/salary-schemas/${schemaId}`);
      if (r.ok) {
        const d = await r.json();
        setComponents(d.components || []);
      }
    } catch (_) { setComponents([]); }
  }, []);

  function startNew() {
    const today   = new Date().toISOString().slice(0, 10);
    const endOfYr = `${new Date().getFullYear()}-12-31`;
    const schemaId = lockedSchemaId || '';
    setDraft({
      _new: true, id: null, level,
      schema_id: schemaId,
      component_id: '',
      region_id: '',
      from_date: today, to_date: endOfYr,
      rate_pct: '', fixed_value: '',
      notes: '',
    });
    fetchComponents(schemaId);
  }
  async function startEdit(r) {
    setDraft({
      _new: false, id: r.id, level,
      schema_id:    r.schema_id || '',
      component_id: r.component_id || '',
      region_id:    r.region_id || '',
      from_date: r.from_date ? String(r.from_date).slice(0, 10) : '',
      to_date:   r.to_date   ? String(r.to_date).slice(0, 10)   : '',
      rate_pct:  r.rate_pct ?? '',
      fixed_value: r.fixed_value ?? '',
      notes:     r.notes || '',
    });
    fetchComponents(r.schema_id);
  }
  function cancelDraft() { setDraft(null); }

  function set(k, v) {
    setDraft(prev => {
      const next = { ...prev, [k]: v };
      if (k === 'schema_id' && v !== prev.schema_id) {
        next.component_id = '';
        fetchComponents(v);
      }
      return next;
    });
  }

  async function save() {
    if (!draft) return;
    if (!draft.schema_id)    { toast.error('Schema is required'); return; }
    if (!draft.component_id) { toast.error('Component is required'); return; }
    if (!draft.region_id)    { toast.error('Region is required'); return; }
    if (!draft.from_date)    { toast.error('From date is required'); return; }
    if ((draft.rate_pct === '' || draft.rate_pct == null) && (draft.fixed_value === '' || draft.fixed_value == null)) {
      toast.error('Either Rate % or Fixed Value is required'); return;
    }

    const body = { ...draft, level };
    const url = draft._new
      ? `/api/salary-schemas/${draft.schema_id}/rates`
      : `/api/salary-schemas/${draft.schema_id}/rates/${draft.id}?level=${level}`;
    const method = draft._new ? 'POST' : 'PUT';
    const r = await apiFetch(url, { method, body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`L${level} rate saved`); setDraft(null); fetchAll(); }
    else      { toast.error(d.error || 'Save failed'); }
  }

  async function remove(rate) {
    const ok = await toast.confirm({
      title: 'Delete rate?',
      body: `Remove this L${level} rate row for ${rate.component_name || 'component'}?`,
      danger: true,
    });
    if (!ok) return;
    const r = await apiFetch(`/api/salary-schemas/${rate.schema_id}/rates/${rate.id}?level=${level}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Rate deleted'); fetchAll(); }
    else      { toast.error('Delete failed'); }
  }

  const lockedSchema = useMemo(
    () => lockedSchemaId ? (schemas.find(s => s.id === lockedSchemaId) || null) : null,
    [schemas, lockedSchemaId]
  );

  const filteredRates = useMemo(() => {
    if (!search) return rates;
    const s = search.toLowerCase();
    return rates.filter(r =>
      (r.schema_name||'').toLowerCase().includes(s)    ||
      (r.component_name||'').toLowerCase().includes(s) ||
      (r.region_name||'').toLowerCase().includes(s)
    );
  }, [rates, search]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <button onClick={() => { window.location.hash = '#/salary-schemas'; }} style={btnGhost}>← Back</button>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>
          Salary Component Masters
          {lockedSchema && (
            <span style={{ marginLeft: 10, fontSize: 13, color: '#94A3B8', fontWeight: 400 }}>
              · {lockedSchema.code} — {lockedSchema.name}
            </span>
          )}
        </h2>
        <input type="text" placeholder="Search schema, component, region..."
          value={search} onChange={e => setSearch(e.target.value)} style={{ ...inp, width: 320 }} />
        <button onClick={startNew} style={btnPrimary}>+ Add L{level} Rate</button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.10)' }}>
        {[1, 2].map(n => (
          <button key={n} onClick={() => { setLevel(n); setDraft(null); }}
            style={{ ...tabBtn, ...(level === n ? tabActive : {}) }}>
            Level {n}
            <span style={{ marginLeft: 8, fontSize: 11, color: '#64748B' }}>
              {n === 1 ? 'employee location region' : 'company region (from Settings)'}
            </span>
          </button>
        ))}
      </div>

      <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14, lineHeight: 1.5 }}>
        Rates apply during payroll runs, in the Compensation Details tab, and in simulations.
        The resolver tries Level 1 (region = employee's location region) first, then Level 2 (region = company region from Administration → Settings).
        If neither matches, the component is treated as <strong>0%</strong> and rendered red with a "No masters found" warning.
        Each row carries either a <strong>Rate %</strong> or a <strong>Fixed Value</strong>.
      </div>

      {draft && (
        <div style={{ ...card, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#E5E7EB', marginBottom: 10 }}>
            {draft._new ? `New L${level} Rate` : `Edit L${level} Rate`}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <Field label="Schema" required>
              <select value={draft.schema_id} onChange={e => set('schema_id', e.target.value)}
                disabled={!!lockedSchemaId} style={{ ...inp, opacity: lockedSchemaId ? 0.6 : 1 }}>
                <option value="">— Select schema —</option>
                {schemas.map(s => <option key={s.id} value={s.id}>{s.code} · {s.name}</option>)}
              </select>
            </Field>
            <Field label="Component" required>
              <select value={draft.component_id} onChange={e => set('component_id', e.target.value)}
                disabled={!draft.schema_id} style={inp}>
                <option value="">{draft.schema_id ? '— Select component —' : 'Pick a schema first'}</option>
                {components.map(c => <option key={c.id} value={c.id}>{c.code || c.name}</option>)}
              </select>
            </Field>
            <RegionPicker label={level === 1 ? 'Region (employee location)' : 'Region (company)'} required
              value={draft.region_id}
              onChange={(v) => set('region_id', v)} placeholder="Select region..." />

            <Field label="Rate %">
              <input type="number" step="0.001" value={draft.rate_pct}
                onChange={e => set('rate_pct', e.target.value)} style={inp} placeholder="e.g. 40 (HRA)" />
            </Field>
            <Field label="Fixed Value">
              <input type="number" step="0.01" value={draft.fixed_value}
                onChange={e => set('fixed_value', e.target.value)} style={inp} placeholder="Flat amount" />
            </Field>
            <div></div>

            <Field label="From Date" required>
              <input type="date" value={draft.from_date} onChange={e => set('from_date', e.target.value)} style={inp} />
            </Field>
            <Field label="To Date">
              <input type="date" value={draft.to_date} onChange={e => set('to_date', e.target.value)} style={inp} />
            </Field>
            <Field label="Notes">
              <input type="text" value={draft.notes} onChange={e => set('notes', e.target.value)} style={inp} placeholder="Optional" />
            </Field>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={cancelDraft} style={btnGhost}>Cancel</button>
            <button onClick={save} style={btnPrimary}>Save L{level} Rate</button>
          </div>
        </div>
      )}

      <div style={card}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }}>Loading rates...</div>
        ) : filteredRates.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
            No L{level} rates yet. Click <strong>+ Add L{level} Rate</strong> to create the first one.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead>
                <tr>
                  {!lockedSchemaId && <th style={th}>Schema</th>}
                  <th style={th}>Component</th>
                  <th style={th}>Region</th>
                  <th style={{ ...th, textAlign: 'right' }}>Rate %</th>
                  <th style={{ ...th, textAlign: 'right' }}>Fixed Value</th>
                  <th style={th}>From</th>
                  <th style={th}>To</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRates.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    {!lockedSchemaId && (
                      <td style={td}>
                        <div style={{ fontSize: 12.5 }}>{r.schema_code}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.schema_name}</div>
                      </td>
                    )}
                    <td style={td}>
                      <div style={{ fontSize: 12.5 }}>{r.component_name}</div>
                      {r.component_code && <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.component_code}</div>}
                    </td>
                    <td style={td}>{r.country_name ? `${r.country_name} · ${r.region_name}` : (r.region_name || '—')}</td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>
                      {r.rate_pct != null ? `${Number(r.rate_pct).toFixed(2)}%` : <span style={{ color: '#64748B' }}>—</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>
                      {r.fixed_value != null ? Number(r.fixed_value).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : <span style={{ color: '#64748B' }}>—</span>}
                    </td>
                    <td style={td}>{fmtDate(r.from_date)}</td>
                    <td style={td}>{r.to_date ? fmtDate(r.to_date) : <span style={{ color: '#64748B' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      <button onClick={() => startEdit(r)} style={btnSmall}>Edit</button>
                      <button onClick={() => remove(r)} style={{ ...btnSmall, color: '#EF4444', marginLeft: 6 }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function parseLockedSchemaId() {
  if (typeof window === 'undefined') return null;
  const m = (window.location.hash || '').match(/\/salary-schemas\/([^/]+)\/component-masters/);
  if (!m) return null;
  if (m[1] === 'component-masters') return null;
  return m[1];
}
function fmtDate(v) {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: '#94A3B8', letterSpacing: '0.4px', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}{required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const card = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: 14 };
const inp  = { padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 13, outline: 'none', width: '100%' };
const btnPrimary = { padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #FF2D7B, #FF6B9D)', color: '#fff' };
const btnGhost   = { padding: '8px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const btnSmall   = { padding: '5px 10px', borderRadius: 5, fontSize: 11.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };

const tabBtn    = { padding: '8px 14px', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
                    background: 'transparent', color: '#94A3B8', borderBottom: '2px solid transparent', marginBottom: -1 };
const tabActive = { color: '#FF2D7B', borderBottom: '2px solid #FF2D7B' };

const tbl = { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12.5 };
const th  = { padding: '10px 10px', textAlign: 'left', background: 'rgba(255,255,255,0.04)',
              color: '#94A3B8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
              borderBottom: '1px solid rgba(255,255,255,0.10)' };
const td  = { padding: '8px 10px', verticalAlign: 'middle' };
